export function normalizeStopOperation(operation) {
  if (operation == null || operation === "") return [];

  if (Array.isArray(operation)) {
    return operation
      .map((op) => String(op).trim().toLowerCase())
      .filter(Boolean);
  }

  const normalized = String(operation).trim().toLowerCase();
  return normalized ? [normalized] : [];
}

export function hasCargaOperation(operation) {
  return normalizeStopOperation(operation).includes("carga");
}

export function hasDescargaOperation(operation) {
  return normalizeStopOperation(operation).includes("descarga");
}

function resolveAttachmentStoppingPointId(
  attachment,
  fallbackStoppingPointId = "",
) {
  if (!attachment || typeof attachment !== "object") {
    return String(fallbackStoppingPointId || "");
  }
  const raw =
    attachment.stoppingPointId ??
    attachment.stopping_point_id ??
    attachment.stoppingPointID ??
    attachment.stopId ??
    attachment.stop_id ??
    attachment.pointId ??
    attachment.point_id ??
    fallbackStoppingPointId ??
    "";
  return String(raw || "");
}

function normalizeFreightAttachment(attachment, fallbackStoppingPointId = "") {
  if (!attachment || typeof attachment !== "object") return null;
  if (!attachment.url && !attachment.key) return null;
  return {
    ...attachment,
    stoppingPointId: resolveAttachmentStoppingPointId(
      attachment,
      fallbackStoppingPointId,
    ),
  };
}

function normalizeAttachmentsList(attachmentsValue, fallbackStoppingPointId = "") {
  const rawAttachments = Array.isArray(attachmentsValue)
    ? attachmentsValue
    : attachmentsValue && typeof attachmentsValue === "object"
      ? Object.values(attachmentsValue)
      : [];

  return rawAttachments
    .map((attachment) =>
      normalizeFreightAttachment(attachment, fallbackStoppingPointId),
    )
    .filter(Boolean);
}

function normalizeInvoicesList(invoicesValue) {
  if (Array.isArray(invoicesValue)) {
    return invoicesValue.filter(
      (item) =>
        item != null &&
        (typeof item === "object"
          ? Boolean(item.url || item.key)
          : String(item).trim() !== ""),
    );
  }
  if (invoicesValue && typeof invoicesValue === "object") {
    return Object.values(invoicesValue).filter(Boolean);
  }
  return [];
}

function getAttachmentsByStoppingPointId(
  attachments = [],
  stoppingPointId = "",
) {
  const normalizedStoppingPointId = String(stoppingPointId || "");
  return normalizeAttachmentsList(attachments).filter(
    (attachment) =>
      resolveAttachmentStoppingPointId(attachment) ===
      normalizedStoppingPointId,
  );
}

function mergeAttachmentLists(firstList = [], secondList = []) {
  const mergedAttachments = [];
  const seenAttachments = new Set();

  [...firstList, ...secondList].forEach((attachment) => {
    const normalizedAttachment = normalizeFreightAttachment(attachment);
    if (!normalizedAttachment) return;

    const attachmentKey =
      normalizedAttachment.id ||
      `${normalizedAttachment.url}|${normalizedAttachment.stoppingPointId}`;

    if (seenAttachments.has(attachmentKey)) return;
    seenAttachments.add(attachmentKey);
    mergedAttachments.push(normalizedAttachment);
  });

  return mergedAttachments;
}

export function parseStopNumericValue(value) {
  if (value === null || value === undefined || value === "") return NaN;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const normalized = String(value).replace(/\./g, "").replace(",", ".");
  const parsed = parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : NaN;
}

export function hasPerStopCargoFreightFields(formFields) {
  return (
    Array.isArray(formFields) &&
    formFields.some((field) => hasCargaOperation(field.operation))
  );
}

function hasStopInvoicesOrDocuments(stop) {
  return (
    normalizeInvoicesList(stop?.invoices).length > 0 ||
    normalizeInvoicesList(stop?.documents).length > 0
  );
}

function hasUnassignedFreightAttachments(freightAttachments = []) {
  return normalizeAttachmentsList(freightAttachments).some(
    (attachment) => !resolveAttachmentStoppingPointId(attachment),
  );
}

/**
 * Lista unificada: attachments do documento do frete + attachments das paradas (app/web).
 */
export function resolveFreightAttachmentsForValidation(
  formFields = [],
  freightRootAttachments = [],
) {
  const lists = [normalizeAttachmentsList(freightRootAttachments)];

  if (Array.isArray(formFields)) {
    formFields.forEach((stop) => {
      const stopId = stop?.id || "";
      lists.push(normalizeAttachmentsList(stop?.attachments, stopId));
    });
  }

  return mergeAttachmentLists(...lists);
}

/**
 * NF-e ou anexo na parada de Carga: invoices, documents, attachments na parada,
 * attachments do frete (web ou app) vinculados por stoppingPointId ou sem vínculo.
 */
export function hasCargaNFeOrAttachment(stop, freightAttachments = []) {
  if (hasStopInvoicesOrDocuments(stop)) return true;

  const stopId = stop?.id || "";
  const fromStop = normalizeAttachmentsList(stop?.attachments, stopId);
  const fromFreight = getAttachmentsByStoppingPointId(
    freightAttachments,
    stopId,
  );
  if (mergeAttachmentLists(fromStop, fromFreight).length > 0) {
    return true;
  }

  if (
    hasCargaOperation(stop?.operation) &&
    hasUnassignedFreightAttachments(freightAttachments)
  ) {
    return true;
  }

  return false;
}

/**
 * Etapa 2: existe NF-e/anexo em alguma parada de Carga do frete.
 */
export function freightHasCargaNFeAttached(formFields, freightAttachments = []) {
  if (!Array.isArray(formFields)) return false;
  return formFields.some(
    (stop) =>
      hasCargaOperation(stop?.operation) &&
      hasCargaNFeOrAttachment(stop, freightAttachments),
  );
}

/**
 * Validação completa (Etapa 2 — após NF-e) para todas as paradas.
 */
export function shouldUseFullStopValidation(stop, context) {
  return freightHasCargaNFeAttached(
    context?.formFields,
    context?.freightAttachments,
  );
}

function isPositiveCargoValue(value) {
  const numericValue = parseStopNumericValue(value);
  return Number.isFinite(numericValue) && numericValue > 0;
}

function validateCargoWeightAndValue(stop, context, stopNum) {
  const errors = [];
  const {
    canViewFreightSection,
    formFields,
    weightCargo,
    valueNF,
  } = context;

  if (!canViewFreightSection) {
    return errors;
  }

  if (hasPerStopCargoFreightFields(formFields)) {
    const weight = parseStopNumericValue(stop.cargo_weight_kg);
    const nf = parseStopNumericValue(stop.cargo_value_nf);
    if (!Number.isFinite(weight) || weight <= 0) {
      errors.push(`Ponto ${stopNum}: Peso da Carga (Kg)`);
    }
    if (!Number.isFinite(nf) || nf <= 0) {
      errors.push(`Ponto ${stopNum}: Valor NF`);
    }
  } else if (!isPositiveCargoValue(weightCargo) || !isPositiveCargoValue(valueNF)) {
    if (!isPositiveCargoValue(weightCargo)) {
      errors.push("Peso da Carga");
    }
    if (!isPositiveCargoValue(valueNF)) {
      errors.push("Valor da NF");
    }
  }

  return errors;
}

/**
 * Etapa 1 — criação/edição sem NF-e no frete: origem/destino com cidade e UF.
 * CNPJ, CEP e endereço completo não bloqueiam.
 */
export function getStoppingPointMinimalCreationErrors(stop, context) {
  const { index } = context;
  const stopNum = stop.stop_order || index + 1;
  const errors = [];

  if (normalizeStopOperation(stop.operation).length === 0) {
    errors.push(`Ponto ${stopNum}: Selecione Carga ou Descarga`);
  }
  if (!stop.city) {
    errors.push(`Ponto ${stopNum}: Cidade`);
  }
  if (!stop.uf) {
    errors.push(`Ponto ${stopNum}: Estado (UF)`);
  }
  if (!stop.date_operation) {
    errors.push(`Ponto ${stopNum}: Data da Coleta/Entrega`);
  }

  return errors;
}

/**
 * Campos obrigatórios antes de anexar NF-e (parada de Carga sem NF-e ainda).
 */
export function getStoppingPointEssentialBeforeNFeErrors(stop, context) {
  const { index } = context;
  const stopNum = stop.stop_order || index + 1;
  const errors = [...getStoppingPointMinimalCreationErrors(stop, context)];

  if (hasCargaOperation(stop.operation)) {
    if (!stop.type) {
      errors.push(`Ponto ${stopNum}: Tipo da Carga (Obrigatório para Carga)`);
    }
    if (!stop.descricao) {
      errors.push(`Ponto ${stopNum}: Descrição (Obrigatório para Carga)`);
    }
    errors.push(...validateCargoWeightAndValue(stop, context, stopNum));
  }

  return errors;
}

/**
 * Etapa 2 — validação completa após NF-e anexada.
 */
export function getStoppingPointFullErrors(stop, context) {
  const { index } = context;
  const stopNum = stop.stop_order || index + 1;
  const errors = [];

  if (!stop.name) {
    errors.push(`Ponto ${stopNum}: Nome/Razão Social`);
  }
  if (!stop.cpf_cnpj) {
    errors.push(`Ponto ${stopNum}: CNPJ/CPF`);
  }
  if (!stop.cep) {
    errors.push(`Ponto ${stopNum}: CEP`);
  }
  if (!stop.logradouro) {
    errors.push(`Ponto ${stopNum}: Endereço`);
  }
  if (!stop.number) {
    errors.push(`Ponto ${stopNum}: Número`);
  }
  if (!stop.city) {
    errors.push(`Ponto ${stopNum}: Cidade`);
  }
  if (!stop.uf) {
    errors.push(`Ponto ${stopNum}: UF`);
  }
  if (!stop.date_operation) {
    errors.push(`Ponto ${stopNum}: Data da Coleta/Entrega`);
  }
  // if (!stop.time_operation) {
  //   errors.push(`Ponto ${stopNum}: Hora da Coleta/Entrega`);
  // }

  if (normalizeStopOperation(stop.operation).length === 0) {
    errors.push(`Ponto ${stopNum}: Selecione Carga ou Descarga`);
  }

  if (hasCargaOperation(stop.operation)) {
    if (!stop.type) {
      errors.push(`Ponto ${stopNum}: Tipo da Carga (Obrigatório para Carga)`);
    }
    if (!stop.descricao) {
      errors.push(`Ponto ${stopNum}: Descrição (Obrigatório para Carga)`);
    }
    errors.push(...validateCargoWeightAndValue(stop, context, stopNum));
  }

  return errors;
}

export function getStoppingPointValidationErrors(stop, context) {
  if (shouldUseFullStopValidation(stop, context)) {
    return getStoppingPointFullErrors(stop, context);
  }
  return getStoppingPointMinimalCreationErrors(stop, context);
}

export function isStoppingPointValid(stop, context) {
  return getStoppingPointValidationErrors(stop, context).length === 0;
}

export function validateCargoFieldsForStops(context) {
  const { formFields, canViewFreightSection, weightCargo, valueNF } = context;

  if (!canViewFreightSection) {
    return { valid: true };
  }

  if (hasPerStopCargoFreightFields(formFields)) {
    const firstInvalid = formFields.findIndex((stop) => {
      if (!hasCargaOperation(stop.operation)) return false;
      const weight = parseStopNumericValue(stop.cargo_weight_kg);
      const nf = parseStopNumericValue(stop.cargo_value_nf);
      return (
        !Number.isFinite(weight) ||
        weight <= 0 ||
        !Number.isFinite(nf) ||
        nf <= 0
      );
    });

    if (firstInvalid >= 0) {
      return {
        valid: false,
        firstInvalidIndex: firstInvalid,
        message:
          "Preencha o Peso da Carga e o Valor NF em todas as paradas de carga.",
        focusId: `weight-cargo-stop-${firstInvalid}`,
      };
    }
    return { valid: true };
  }

  return { valid: true };
}

export function getFirstCargaStopIndex(formFields = []) {
  if (!Array.isArray(formFields) || formFields.length === 0) return -1;

  const sorted = formFields
    .map((stop, index) => ({
      index,
      order: Number(stop?.stop_order) || index + 1,
      stop,
    }))
    .filter(({ stop }) => hasCargaOperation(stop?.operation))
    .sort((a, b) => a.order - b.order);

  return sorted.length > 0 ? sorted[0].index : -1;
}

export function isLinkedDriverFirstCargaStop(
  stop,
  stopIndex,
  formFields,
  hasLinkedDriver = false,
) {
  if (!hasLinkedDriver) return false;
  const firstCargaIndex = getFirstCargaStopIndex(formFields);
  return (
    firstCargaIndex >= 0 &&
    stopIndex === firstCargaIndex &&
    hasCargaOperation(stop?.operation)
  );
}

const STOP_LINKED_DRIVER_FIRST_CARGA_BASE_FIELDS = new Set([
  "operation",
  "name",
  "cpf_cnpj",
  "cep",
  "logradouro",
  "number",
  "city",
  "uf",
  "date_operation",
]);

function pushLinkedDriverFirstCargaFieldErrors(stop, context) {
  const { index } = context;
  const stopNum = stop.stop_order || index + 1;
  const errors = [];

  if (normalizeStopOperation(stop.operation).length === 0) {
    errors.push(`Ponto ${stopNum}: Selecione Carga ou Descarga`);
  }
  if (!stop.name) {
    errors.push(`Ponto ${stopNum}: Nome/Razão Social`);
  }
  if (!stop.cpf_cnpj) {
    errors.push(`Ponto ${stopNum}: CNPJ/CPF`);
  }
  if (!stop.cep) {
    errors.push(`Ponto ${stopNum}: CEP`);
  }
  if (!stop.logradouro) {
    errors.push(`Ponto ${stopNum}: Endereço`);
  }
  if (!stop.number) {
    errors.push(`Ponto ${stopNum}: Número`);
  }
  if (!stop.city) {
    errors.push(`Ponto ${stopNum}: Cidade`);
  }
  if (!stop.uf) {
    errors.push(`Ponto ${stopNum}: UF`);
  }
  if (!stop.date_operation) {
    errors.push(`Ponto ${stopNum}: Data da Coleta/Entrega`);
  }

  // if (
  //   shouldUseFullStopValidation(stop, context) &&
  //   !stop.time_operation
  // ) {
  //   errors.push(`Ponto ${stopNum}: Hora da Coleta/Entrega`);
  // }

  if (hasCargaOperation(stop.operation)) {
    if (!stop.type) {
      errors.push(`Ponto ${stopNum}: Tipo da Carga (Obrigatório para Carga)`);
    }
    if (!stop.descricao) {
      errors.push(`Ponto ${stopNum}: Descrição (Obrigatório para Carga)`);
    }
    errors.push(...validateCargoWeightAndValue(stop, context, stopNum));
  }

  return errors;
}

/**
 * Validação reforçada do primeiro ponto de Carga quando há motorista vinculado.
 * Exige endereço completo (CNPJ, nome, CEP, endereço, número, cidade, UF)
 * independentemente da etapa de NF-e.
 */
export function getLinkedDriverFirstCargaStopErrors(stop, context) {
  return pushLinkedDriverFirstCargaFieldErrors(stop, context);
}

export function getAllStoppingPointErrors(formFields, context, options = {}) {
  const { hasLinkedDriver = false } = options;

  if (!formFields || formFields.length === 0) {
    return ["Pontos de Parada (Adicione pelo menos um ponto)"];
  }

  const firstCargaIndex = hasLinkedDriver
    ? getFirstCargaStopIndex(formFields)
    : -1;

  if (hasLinkedDriver && firstCargaIndex < 0) {
    return [
      "Primeiro ponto de parada do tipo Carga (obrigatório ao vincular motorista)",
    ];
  }

  const errors = [];
  formFields.forEach((stop, index) => {
    const stopContext = { ...context, index };
    if (hasLinkedDriver && index === firstCargaIndex) {
      errors.push(
        ...getLinkedDriverFirstCargaStopErrors(stop, stopContext),
      );
      return;
    }
    errors.push(...getStoppingPointValidationErrors(stop, stopContext));
  });
  return errors;
}

/**
 * Validação obrigatória ao liberar frete: tipo de parada, cidade, UF e data de coleta
 * em todos os pontos de parada.
 */
export function getAllStoppingPointErrorsForRelease(formFields = []) {
  if (!formFields || formFields.length === 0) {
    return ["Pontos de Parada (Adicione pelo menos um ponto)"];
  }

  const errors = [];
  formFields.forEach((stop, index) => {
    const stopNum = stop.stop_order || index + 1;

    if (normalizeStopOperation(stop.operation).length === 0) {
      errors.push(`Ponto ${stopNum}: Tipo de Parada`);
    }
    if (!stop.city || String(stop.city).trim() === "") {
      errors.push(`Ponto ${stopNum}: Cidade`);
    }
    if (!stop.uf || String(stop.uf).trim() === "") {
      errors.push(`Ponto ${stopNum}: UF`);
    }
    if (!stop.date_operation) {
      errors.push(`Ponto ${stopNum}: Data Coleta`);
    }
  });

  return errors;
}

export function validateFirstCargaStopWhenDriverLinked(
  formFields,
  context,
  options = {},
) {
  const { hasLinkedDriver = false } = options;
  if (!hasLinkedDriver) {
    return { valid: true, errors: [], firstCargaStopIndex: -1, message: "" };
  }

  const firstCargaIndex = getFirstCargaStopIndex(formFields);
  if (firstCargaIndex < 0) {
    const errors = [
      "Primeiro ponto de parada do tipo Carga (obrigatório ao vincular motorista)",
    ];
    return {
      valid: false,
      errors,
      firstCargaStopIndex: -1,
      message: errors[0],
    };
  }

  const stop = formFields[firstCargaIndex];
  const stopNum = stop.stop_order || firstCargaIndex + 1;
  const errors = getLinkedDriverFirstCargaStopErrors(stop, {
    ...context,
    index: firstCargaIndex,
  });

  const pendingFields = errors.map((error) =>
    error.replace(/^Ponto \d+: /, ""),
  );

  return {
    valid: errors.length === 0,
    errors,
    firstCargaStopIndex: firstCargaIndex,
    message:
      errors.length > 0
        ? `Com motorista vinculado, preencha o primeiro ponto de Carga (Parada ${stopNum}): ${pendingFields.join(", ")}.`
        : "",
  };
}

const ESSENTIAL_BEFORE_NFE_LABELS = {
  operation: "Tipo do ponto de parada (Carga/Descarga)",
  city: "Cidade",
  uf: "Estado",
  date_operation: "Data Coleta",
  vehicle: "Veículo",
  bodywork: "Carroceria",
  merchandise_type: "Tipo de Mercadoria",
  merchandise_description: "Descrição da Mercadoria",
  weight: "Peso",
  value: "Valor",
};

export function validateEssentialBeforeCargaNFeAttachment({
  stop,
  stopIndex = 0,
  context,
  tipoVeiculo,
  typeBodyworkList,
}) {
  if (!stop || !hasCargaOperation(stop.operation)) {
    return {
      valid: false,
      errors: [ESSENTIAL_BEFORE_NFE_LABELS.operation],
      message:
        "Selecione a operação Carga no ponto de parada antes de importar a NF-e.",
    };
  }

  if (hasCargaNFeOrAttachment(stop, context?.freightAttachments)) {
    return { valid: true, errors: [] };
  }

  const errors = [];
  const essentialErrors = getStoppingPointEssentialBeforeNFeErrors(stop, {
    ...context,
    index: stopIndex,
  });

  essentialErrors.forEach((err) => {
    if (err.includes("Carga ou Descarga")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.operation);
    } else if (err.includes("Cidade")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.city);
    } else if (err.includes("UF") || err.includes("Estado")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.uf);
    } else if (err.includes("Data")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.date_operation);
    } else if (err.includes("Tipo da Carga")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.merchandise_type);
    } else if (err.includes("Descrição")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.merchandise_description);
    } else if (err.includes("Peso")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.weight);
    } else if (err.includes("Valor NF") || err.includes("Valor da NF")) {
      errors.push(ESSENTIAL_BEFORE_NFE_LABELS.value);
    }
  });

  if (!tipoVeiculo?.dados?.some((item) => item.selected)) {
    errors.push(ESSENTIAL_BEFORE_NFE_LABELS.vehicle);
  }
  if (!typeBodyworkList?.dados?.some((item) => item.selected)) {
    errors.push(ESSENTIAL_BEFORE_NFE_LABELS.bodywork);
  }

  const uniqueErrors = [...new Set(errors)];

  return {
    valid: uniqueErrors.length === 0,
    errors: uniqueErrors,
    message:
      uniqueErrors.length > 0
        ? `Antes de importar a NF-e, preencha: ${uniqueErrors.join(", ")}.`
        : "",
  };
}

export function getFreightValidationStageLabel(context) {
  return freightHasCargaNFeAttached(
    context?.formFields,
    context?.freightAttachments,
  )
    ? "Após anexo da NF-e (validação completa)"
    : "Criação inicial (cidade e estado; endereço completo opcional)";
}

const STOP_ALWAYS_REQUIRED_FIELDS = new Set([
  "operation",
  "city",
  "uf",
  "date_operation",
]);

const STOP_FULL_VALIDATION_FIELDS = new Set([
  "name",
  "cpf_cnpj",
  "cep",
  "logradouro",
  "number",
  "time_operation",
]);

const STOP_CARGA_FIELDS = new Set(["type", "descricao"]);

const STOP_CARGA_FREIGHT_FIELDS = new Set([
  "cargo_weight_kg",
  "cargo_value_nf",
]);

function isLinkedDriverFirstCargaFieldRequired(fieldName, stop, context) {
  const fullValidation = shouldUseFullStopValidation(stop, context);
  const isCarga = hasCargaOperation(stop?.operation);
  const { canViewFreightSection, formFields } = context || {};

  if (STOP_LINKED_DRIVER_FIRST_CARGA_BASE_FIELDS.has(fieldName)) {
    return true;
  }

  if (fullValidation && fieldName === "time_operation") {
    return true;
  }

  if (isCarga && STOP_CARGA_FIELDS.has(fieldName)) {
    return true;
  }

  if (
    isCarga &&
    canViewFreightSection &&
    hasPerStopCargoFreightFields(formFields) &&
    STOP_CARGA_FREIGHT_FIELDS.has(fieldName)
  ) {
    return true;
  }

  return false;
}

/**
 * Indica se um campo da parada deve exibir asterisco obrigatório,
 * alinhado às etapas de validação (criação inicial vs. após NF-e).
 */
export function isStopFieldRequired(fieldName, stop, context) {
  const {
    canViewFreightSection,
    formFields,
    index,
    hasLinkedDriver = false,
  } = context || {};

  if (
    isLinkedDriverFirstCargaStop(stop, index, formFields, hasLinkedDriver)
  ) {
    return isLinkedDriverFirstCargaFieldRequired(fieldName, stop, context);
  }

  const fullValidation = shouldUseFullStopValidation(stop, context);
  const isCarga = hasCargaOperation(stop?.operation);

  if (STOP_ALWAYS_REQUIRED_FIELDS.has(fieldName)) {
    return true;
  }

  if (fullValidation && STOP_FULL_VALIDATION_FIELDS.has(fieldName)) {
    return true;
  }

  if (isCarga && STOP_CARGA_FIELDS.has(fieldName)) {
    return true;
  }

  if (
    isCarga &&
    canViewFreightSection &&
    hasPerStopCargoFreightFields(formFields) &&
    STOP_CARGA_FREIGHT_FIELDS.has(fieldName)
  ) {
    return true;
  }

  return false;
}

/**
 * Indica se um campo do frete (fora das paradas) deve exibir asterisco obrigatório.
 */
export function isFreightFormFieldRequired(fieldName, context, options = {}) {
  const {
    canViewFreightSection = false,
    canViewFreightDriver = false,
    uidDriver = "",
    hasCargaStopsOnFreight = false,
  } = options;

  const hasNFe = freightHasCargaNFeAttached(
    context?.formFields,
    context?.freightAttachments,
  );
  const perStopCargo = hasPerStopCargoFreightFields(context?.formFields);

  switch (fieldName) {
    case "payingCustomer":
    case "cnpjPayingCustomer":
      return true;
    case "vehicle":
    case "bodywork":
      return hasNFe || hasCargaStopsOnFreight;
    case "occupation":
    case "freeOfCharge":
      return hasNFe;
    case "seller":
    case "valueFreightage":
      return hasNFe && canViewFreightSection;
    case "weightCargo":
    case "valueNF":
      return (
        canViewFreightSection &&
        !perStopCargo &&
        (hasNFe || hasCargaStopsOnFreight)
      );
    case "valueDriverInitial":
    case "valueDriverFinal":
    case "valueNegotiated":
    case "paymentCondition":
      return canViewFreightDriver && Boolean(uidDriver);
    default:
      return false;
  }
}
